import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Building2, CheckCircle, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";
import SiteLink from "@/components/SiteLink";

interface InvitationValidation {
  valid: boolean;
  email: string;
  /** Whether a Larasoft account already exists for `email` (cp: web #1345).
   *  Picks the screen's ONE action: Sign In when true, Create Account when false. */
  has_account: boolean;
  organisation_name: string;
  role: string;
  inviter_name: string;
  expires_at: string;
}

interface AcceptResponse {
  success: boolean;
  message: string;
  organisation_id: string | null;
  organisation_name: string | null;
}

export const Route = createFileRoute("/portal_/invite/accept")({
  component: InviteAcceptPage,
});

function InviteAcceptPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");
  const { token: authToken, user, logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationValidation | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptResult, setAcceptResult] = useState<AcceptResponse | null>(null);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError("No invitation token provided");
      setLoading(false);
      return;
    }

    portalFetch<InvitationValidation>(
      `/api/v1/invitations/validate/${token}`,
      {},
    )
      .then((data) => {
        setInvitation(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const detail = (err as { details?: { detail?: string } })?.details
          ?.detail;
        setError(detail || "Invalid or expired invitation");
        setLoading(false);
      });
  }, [token]);

  const handleAccept = async () => {
    if (!authToken || !token) return;
    setAccepting(true);
    try {
      const result = await portalFetch<AcceptResponse>(
        "/api/v1/invitations/accept",
        {
          token: authToken,
          method: "POST",
          body: { token },
        },
      );
      setAcceptResult(result);
      setAccepted(true);
    } catch (err: unknown) {
      const detail = (err as { details?: { detail?: string } })?.details
        ?.detail;
      setError(detail || "Failed to accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  const roleLabel: Record<string, string> = {
    org_member: "Member",
    org_admin: "Admin",
    org_owner: "Owner",
  };

  const returnUrl = `/portal/invite/accept?token=${token}`;

  // ONE action per state (web #1345): an invitee whose email already has an
  // account must be sent to Sign In, never offered Create Account alongside it.
  const signInHref = invitation
    ? `/portal/sign-in?redirect=${encodeURIComponent(returnUrl)}&inviteEmail=${encodeURIComponent(invitation.email)}`
    : "";
  const signUpHref = invitation
    ? `/portal/sign-up?returnTo=${encodeURIComponent(returnUrl)}&inviteEmail=${encodeURIComponent(invitation.email)}&inviteOrg=${encodeURIComponent(invitation.organisation_name)}`
    : "";
  const nextHref = invitation?.has_account ? signInHref : signUpHref;

  const handleSwitchAccount = async () => {
    await logout();
    window.location.assign(nextHref);
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-10 sm:px-6">
        <SiteLink
          href="/"
          className="inline-flex items-center gap-4 rounded-3xl border border-slate-200 bg-white px-4 py-2 shadow-sm transition hover:border-slate-300"
          style={{ width: "fit-content" }}
        >
          <img
            src="/Larasoft Logo-landscape.svg"
            alt="Larasoft"
            width={180}
            height={64}
            className="h-12"
            style={{ width: "auto" }}
          />
          <span className="rounded-full bg-brand-blue-light/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.3em] text-brand-blue-dark">
            Client portal
          </span>
        </SiteLink>

        <div className="mt-12 flex justify-center">
          <Card className="w-full max-w-md">
            {loading ? (
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </CardContent>
            ) : error ? (
              <>
                <CardHeader className="text-center">
                  <XCircle className="mx-auto h-12 w-12 text-destructive" />
                  <CardTitle className="mt-4">Invitation Error</CardTitle>
                  <CardDescription>{error}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <SiteLink href="/portal/sign-in">
                    <Button variant="outline">Go to Sign In</Button>
                  </SiteLink>
                </CardContent>
              </>
            ) : accepted && acceptResult ? (
              <>
                <CardHeader className="text-center">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
                  <CardTitle className="mt-4">Welcome!</CardTitle>
                  <CardDescription>{acceptResult.message}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Button
                    onClick={() =>
                      navigate({
                        to: "/portal",
                      })
                    }
                  >
                    Go to Portal
                  </Button>
                </CardContent>
              </>
            ) : invitation ? (
              <>
                <CardHeader className="text-center">
                  <Building2 className="mx-auto h-12 w-12 text-brand-blue-medium" />
                  <CardTitle className="mt-4">
                    You&apos;re invited to join
                  </CardTitle>
                  <CardDescription className="text-lg font-semibold text-foreground">
                    {invitation.organisation_name}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3 rounded-lg bg-slate-50 p-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Invited by</span>
                      <span className="font-medium">
                        {invitation.inviter_name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Role</span>
                      <Badge variant="secondary">
                        {roleLabel[invitation.role] || invitation.role}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email</span>
                      <span className="font-medium">{invitation.email}</span>
                    </div>
                  </div>

                  {authToken && user ? (
                    user.email?.toLowerCase() === invitation.email.toLowerCase() ? (
                      <Button
                        className="w-full"
                        onClick={handleAccept}
                        disabled={accepting}
                      >
                        {accepting && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Accept Invitation
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <div
                          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                          data-testid="invite-wrong-account"
                        >
                          <p>
                            This invitation is for{" "}
                            <span className="font-semibold">{invitation.email}</span>,
                            but you are signed in as{" "}
                            <span className="font-semibold">{user.email}</span>.
                          </p>
                          <p className="mt-1">
                            {invitation.has_account
                              ? `Sign out and sign in as ${invitation.email} to accept.`
                              : `Sign out and create an account for ${invitation.email} to accept.`}
                          </p>
                        </div>
                        <Button className="w-full" onClick={handleSwitchAccount}>
                          {invitation.has_account
                            ? "Sign out & Sign In"
                            : "Sign out & Create Account"}
                        </Button>
                      </div>
                    )
                  ) : (
                    <div className="space-y-3">
                      <p
                        className="text-center text-sm text-muted-foreground"
                        data-testid="invite-next-step"
                      >
                        {invitation.has_account ? (
                          <>
                            You already have a Larasoft account for{" "}
                            <span className="font-semibold">{invitation.email}</span>.{" "}
                            Sign in to accept.
                          </>
                        ) : (
                          <>
                            Create a Larasoft account for{" "}
                            <span className="font-semibold">{invitation.email}</span>{" "}
                            to accept.
                          </>
                        )}
                      </p>
                      <SiteLink href={nextHref} className="block">
                        <Button className="w-full">
                          {invitation.has_account ? "Sign In" : "Create Account"}
                        </Button>
                      </SiteLink>
                    </div>
                  )}
                </CardContent>
              </>
            ) : null}
          </Card>
        </div>
      </div>
    </div>
  );
}
